import { useState, useRef, useEffect } from 'react';
import { Conflict } from '../types';

interface ConflictPanelProps {
  conflicts: Conflict[];
  onConflictClick?: (nodeIds: string[]) => void;
}

export default function ConflictPanel({ conflicts, onConflictClick }: ConflictPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [position, setPosition] = useState({ x: 20, y: 20 }); // x from right, y from bottom
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging if clicking within the conflict panel
    if (!(e.target as HTMLElement).closest('.conflict-panel')) {
      return;
    }

    // Don't allow dragging from conflict items (they have their own click handlers)
    if ((e.target as HTMLElement).closest('.conflict-item')) {
      return;
    }

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragRef.current) return;

      const deltaX = dragRef.current.startX - e.clientX; // Reverse for right positioning
      const deltaY = dragRef.current.startY - e.clientY; // Reverse for bottom positioning

      setPosition({
        x: Math.max(0, dragRef.current.initialX + deltaX),
        y: Math.max(0, dragRef.current.initialY + deltaY),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragRef.current = null;
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  if (conflicts.length === 0) {
    return (
      <div
        className="conflict-panel"
        style={{
          position: 'absolute',
          bottom: `${position.y}px`,
          right: `${position.x}px`,
          width: '300px',
          background: '#f0fdf4',
          border: '2px solid #86efac',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleMouseDown}
      >
        <div
          style={{
            padding: '12px 16px',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#166534' }}>
              ✅ No Conflicts
            </span>
          </div>
          <span style={{ fontSize: '12px', color: '#166534' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        {isExpanded && (
          <div style={{ padding: '0 16px 16px 16px', fontSize: '12px', color: '#15803d' }}>
            All dependencies are properly structured with no circular references or multiple conflicting dependencies.
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="conflict-panel"
      style={{
        position: 'absolute',
        bottom: `${position.y}px`,
        right: `${position.x}px`,
        width: '320px',
        maxHeight: '70vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        border: '2px solid #ef4444',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: '#fee2e2',
          borderBottom: isExpanded ? '1px solid #fca5a5' : 'none',
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc2626' }}>
            ⚠️ {conflicts.length} Conflict{conflicts.length > 1 ? 's' : ''}
          </div>
          {isExpanded && (
            <div style={{ fontSize: '10px', color: '#991b1b', marginTop: '2px' }}>
              Click conflict to highlight
            </div>
          )}
        </div>
        <span style={{ fontSize: '12px', color: '#dc2626' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>

      {/* Conflict List */}
      {isExpanded && (
        <div style={{ padding: '8px', overflowY: 'auto', flex: 1 }}>
          {conflicts.map((conflict, idx) => (
            <div
              key={idx}
              className="conflict-item"
              onClick={(e) => {
                e.stopPropagation();
                onConflictClick?.(conflict.affectedNodes);
              }}
              style={{
                padding: '12px',
                marginBottom: '8px',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fee2e2';
                e.currentTarget.style.transform = 'translateX(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fef2f2';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              {/* Conflict Type Badge */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    background: conflict.type === 'circular' ? '#dc2626' : '#f59e0b',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    marginRight: '8px',
                  }}
                >
                  {conflict.type === 'circular' ? 'CIRCULAR' : 'MULTIPLE DEPS'}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color:
                      conflict.severity === 'high'
                        ? '#dc2626'
                        : conflict.severity === 'medium'
                        ? '#f59e0b'
                        : '#64748b',
                  }}
                >
                  {conflict.severity.toUpperCase()}
                </span>
              </div>

              {/* Description */}
              <div style={{ fontSize: '12px', color: '#1e293b', lineHeight: '1.5', marginBottom: '8px' }}>
                {conflict.description}
              </div>

              {/* Affected Nodes */}
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                <strong>Affected nodes:</strong>{' '}
                {conflict.affectedNodes.map((nodeId, i) => (
                  <span key={i}>
                    <code
                      style={{
                        background: '#e2e8f0',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontSize: '9px',
                      }}
                    >
                      {nodeId}
                    </code>
                    {i < conflict.affectedNodes.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
